// Dated availability editor data (AVL-001–AVL-012).
//
// The UI keeps local-date intent until submission. Block resolution and exact-window
// validation both call the domain kernel, so DST and overnight rules cannot drift
// from matching or the Convex mutations.

import {
  instant,
  localDate,
  localDateOf,
  nextLocalDate,
  resolveBlock,
  resolveWallClock,
  startOfLocalWeek,
  timeZone,
  validateWindow,
  type BlockDefinitions,
  type LocalDate,
  type ResolvedBlock,
  type TimeBlockId,
  type TimeZoneId,
} from "@herefor/domain-kernel";

export type AvailabilityHelpMode = "in_person" | "driving" | "phone_or_online";
export type AvailabilitySourceChannel = "phone" | "message" | "in_person";
export type AvailabilityEditorMode = "blocks" | "exact";

export interface BlockSelection {
  readonly dateLocal: string;
  readonly blockId: TimeBlockId;
}

export interface AvailabilityDraft {
  readonly mode: AvailabilityEditorMode;
  readonly weekStartLocal: string;
  readonly selectedBlocks: readonly BlockSelection[];
  readonly exactStartsAtLocal: string;
  readonly exactEndsAtLocal: string;
  readonly helpModes: readonly AvailabilityHelpMode[];
}

export type AvailabilityMutationEntry =
  | {
      readonly kind: "block";
      readonly dateLocal: string;
      readonly blockId: TimeBlockId;
      readonly helpModes: readonly AvailabilityHelpMode[];
    }
  | {
      readonly kind: "exact";
      readonly startsAt: number;
      readonly endsAt: number;
      readonly helpModes: readonly AvailabilityHelpMode[];
    };

export type AvailabilityDraftIssue =
  | "invalid_week_start"
  | "week_must_start_monday"
  | "choose_time"
  | "choose_help_mode"
  | "date_outside_week"
  | "exact_time_unreadable"
  | "end_not_after_start"
  | "overnight_not_supported";

export type AvailabilityDraftResolution =
  | {
      readonly ok: true;
      readonly weekStartLocal: string;
      readonly weekEndLocal: string;
      readonly entries: readonly AvailabilityMutationEntry[];
    }
  | { readonly ok: false; readonly issue: AvailabilityDraftIssue };

export interface AvailabilityWeekDay {
  readonly dateLocal: string;
  readonly blocks: readonly ResolvedBlock[];
}

export interface AvailabilityBatchExpiry {
  readonly endDateLocal: LocalDate;
  readonly expiresAt: number;
  readonly expired: boolean;
}

export function availabilityWeek(
  weekStartLocal: string,
  zone: TimeZoneId,
  definitions: BlockDefinitions,
): readonly AvailabilityWeekDay[] {
  const weekStart = localDate(weekStartLocal);
  const days: AvailabilityWeekDay[] = [];
  let date = weekStart;
  for (let day = 0; day < 7; day += 1) {
    days.push({
      dateLocal: date,
      blocks: (["morning", "afternoon", "evening"] as const).map((blockId) =>
        resolveBlock(blockId, date, zone, definitions),
      ),
    });
    date = nextLocalDate(date);
  }
  return days;
}

export function availabilityWeekEnd(weekStartLocal: string): LocalDate {
  let date = localDate(weekStartLocal);
  for (let day = 0; day < 6; day += 1) date = nextLocalDate(date);
  return date;
}

/**
 * Project a batch's expiry from its final actual window. This is the same instant
 * the kernel stops matching that final window; the projection never mutates it.
 */
export function availabilityBatchExpiry(
  latestEndsAt: number,
  now: number,
  zone: TimeZoneId,
): AvailabilityBatchExpiry {
  return {
    endDateLocal: localDateOf(instant(latestEndsAt), zone),
    expiresAt: latestEndsAt,
    expired: now >= latestEndsAt,
  };
}

/**
 * The prompt window is symmetric around expiry: it starts `horizonDays` before the
 * week ends and remains for the same number of local dates afterwards. This keeps a
 * recently-ended week visible without turning an old record into a recurring nudge.
 */
export function isWithinAvailabilityRenewalHorizon(
  expiryDateLocal: string,
  now: number,
  zone: TimeZoneId,
  horizonDays: number,
): boolean {
  if (!Number.isSafeInteger(horizonDays) || horizonDays < 0) return false;
  const today = localDateOf(instant(now), zone);
  return (
    localDate(expiryDateLocal) <= addLocalDays(today, horizonDays) &&
    today <= addLocalDays(localDate(expiryDateLocal), horizonDays)
  );
}

/** A renewal always starts a new dated week after the preserved source batch. */
export function nextAvailabilityWeekStart(weekStartLocal: string): LocalDate {
  return nextLocalDate(availabilityWeekEnd(weekStartLocal));
}

export function normalizeWeekStart(dateLocal: string): string {
  return startOfLocalWeek(localDate(dateLocal));
}

function addLocalDays(date: LocalDate, days: number): LocalDate {
  let result = date;
  for (let day = 0; day < days; day += 1) result = nextLocalDate(result);
  return result;
}

/** A display/test seam that still delegates instant-to-local-date conversion to the kernel. */
export function localDateForInstant(at: number, zoneName: string): string {
  return String(localDateOf(instant(at), timeZone(zoneName)));
}

export function resolveAvailabilityDraft(
  draft: AvailabilityDraft,
  zone: TimeZoneId,
  definitions: BlockDefinitions,
): AvailabilityDraftResolution {
  let weekStart: LocalDate;
  try {
    weekStart = localDate(draft.weekStartLocal);
  } catch {
    return { ok: false, issue: "invalid_week_start" };
  }
  if (startOfLocalWeek(weekStart) !== weekStart) {
    return { ok: false, issue: "week_must_start_monday" };
  }
  if (draft.helpModes.length === 0) return { ok: false, issue: "choose_help_mode" };

  const allowedDates = new Set(
    availabilityWeek(weekStart, zone, definitions).map((day) => day.dateLocal),
  );
  const helpModes = [...new Set(draft.helpModes)];
  let entries: readonly AvailabilityMutationEntry[];

  if (draft.mode === "blocks") {
    if (draft.selectedBlocks.length === 0) return { ok: false, issue: "choose_time" };
    if (draft.selectedBlocks.some((selection) => !allowedDates.has(selection.dateLocal))) {
      return { ok: false, issue: "date_outside_week" };
    }
    entries = draft.selectedBlocks.map((selection) => {
      // Resolve here as well as on the server. The selected lane's displayed window
      // and the mutation therefore share the exact per-date DST interpretation.
      resolveBlock(selection.blockId, localDate(selection.dateLocal), zone, definitions);
      return { kind: "block", ...selection, helpModes };
    });
  } else {
    const startsAt = parseLocalDateTime(draft.exactStartsAtLocal, zone);
    const endsAt = parseLocalDateTime(draft.exactEndsAtLocal, zone);
    if (startsAt === null || endsAt === null) {
      return { ok: false, issue: "exact_time_unreadable" };
    }
    if (!allowedDates.has(String(localDateOf(instant(startsAt), zone)))) {
      return { ok: false, issue: "date_outside_week" };
    }
    const validation = validateWindow(
      { startsAt: instant(startsAt), endsAt: instant(endsAt) },
      zone,
    );
    if (validation.kind === "rejected") {
      return {
        ok: false,
        issue:
          validation.rejection.kind === "crosses_day_boundary"
            ? "overnight_not_supported"
            : "end_not_after_start",
      };
    }
    entries = [{ kind: "exact", startsAt, endsAt, helpModes }];
  }

  return {
    ok: true,
    weekStartLocal: weekStart,
    weekEndLocal: availabilityWeekEnd(weekStart),
    entries,
  };
}

function parseLocalDateTime(value: string, zone: TimeZoneId): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(value.trim());
  if (match === null) return null;
  const [, year, month, day, hour, minute] = match;
  const resolved = resolveWallClock(
    {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
    },
    zone,
  );
  return resolved.kind === "ok" ? resolved.instant : null;
}
